import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { CardTitle } from "components/ui/card";
import { Label } from "components/ui/label";
import { Button } from "components/ui/button";
import { FlowPicker } from "components/admin/FlowPicker";
import { useToast } from "components/ui/use-toast";
import { exportFlow, adminContent } from "../../services/api";
import { Download, Loader2 } from "lucide-react";

interface AdminFlow {
  id: number;
  name: string;
  description?: string;
  is_active: boolean;
  frequency?: string | null;
  is_quiz: boolean;
  passing_score_percentage?: number | null;
  max_attempts?: number | null;
  estimated_duration_minutes?: number | null;
}

const FlowExport = () => {
  const { addToast } = useToast();
  const [flows, setFlows] = useState<AdminFlow[]>([]);
  const [selectedFlowId, setSelectedFlowId] = useState<string>("");
  const [flowSearchTerm, setFlowSearchTerm] = useState("");
  const [isLoadingFlows, setIsLoadingFlows] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const flowsRequestRef = useRef(0);
  const selectedFlowRef = useRef<AdminFlow | null>(null);

  const loadFlows = useCallback(async (searchTerm: string = "") => {
    const requestId = ++flowsRequestRef.current;
    setIsLoadingFlows(true);
    try {
      const summaries: AdminFlow[] = [];
      let page = 1;
      let hasNextPage: boolean;
      do {
        const response = await adminContent.flows.listSummary({
          search: searchTerm,
          page_size: 100,
          page,
        });
        if (requestId !== flowsRequestRef.current) return;
        summaries.push(...response.data.results);
        hasNextPage = Boolean(response.data.next);
        page += 1;
      } while (hasNextPage);
      setFlows(summaries);
    } catch (error) {
      if (requestId !== flowsRequestRef.current) return;
      console.error("Failed to load flows", error);
      addToast({
        title: "Unable to load modules",
        description: "Please refresh the page or try again later.",
        variant: "destructive",
      });
    } finally {
      if (requestId === flowsRequestRef.current) setIsLoadingFlows(false);
    }
  }, [addToast]);

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      loadFlows(flowSearchTerm);
    }, 300);

    return () => {
      clearTimeout(timeoutId);
      flowsRequestRef.current += 1;
    };
  }, [flowSearchTerm, loadFlows]);

  const selectedFlow = useMemo(
    () => flows.find((flow) => flow.id.toString() === selectedFlowId) ?? selectedFlowRef.current,
    [flows, selectedFlowId]
  );

  const flowOptions = useMemo(
    () =>
      flows.map((flow) => ({
        value: flow.id.toString(),
        label: flow.name,
        searchText: `${flow.name} ${flow.description ?? ""}`.trim(),
        isQuiz: flow.is_quiz,
      })),
    [flows]
  );

  const flowEmptyMessage = isLoadingFlows
    ? "Loading modules..."
    : flows.length === 0 && flowSearchTerm
      ? `No modules found matching "${flowSearchTerm}"`
      : "No modules available";

  const handleExport = async () => {
    if (!selectedFlowId) {
      addToast({
        title: "No module selected",
        description: "Please select a module to export.",
        variant: "destructive",
      });
      return;
    }

    setIsExporting(true);
    try {
      const response = await exportFlow(Number(selectedFlowId));
      const blob = new Blob([response.data], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;

      const flowName = selectedFlow?.name || `flow_${selectedFlowId}`;
      const sanitizedName = flowName.replace(/[^a-z0-9_-]/gi, '_').toLowerCase();
      a.download = `${sanitizedName}.csv`;

      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);

      addToast({
        title: "Export successful",
        description: "Module has been exported to CSV.",
      });
    } catch (error: any) {
      console.error("Export failed:", error);
      addToast({
        title: "Export failed",
        description: error?.response?.data?.error || "Failed to export the module.",
        variant: "destructive",
      });
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <div className="space-y-6">
      <CardTitle
        title="Module Export"
        subtitle="Download a CSV file containing all questions from a selected module"
        size="default"
      />

      <div className="space-y-2">
        <Label htmlFor="flow-select">Select Module</Label>
        <FlowPicker
          value={selectedFlowId}
          onValueChange={(value) => {
            selectedFlowRef.current = flows.find((flow) => flow.id.toString() === value) ?? null;
            setSelectedFlowId(value);
          }}
          options={flowOptions}
          placeholder="Choose a module to export"
          triggerId="flow-select"
          searchTerm={flowSearchTerm}
          onSearchTermChange={setFlowSearchTerm}
          loading={isLoadingFlows}
          emptyMessage={flowEmptyMessage}
        />
      </div>

      <Button
        onClick={handleExport}
        disabled={!selectedFlowId || isExporting}
        className="w-full md:w-auto"
      >
        {isExporting ? (
          <>
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            Exporting...
          </>
        ) : (
          <>
            <Download className="mr-2 h-4 w-4" />
            Export Module
          </>
        )}
      </Button>
    </div>
  );
};

export default FlowExport;
